import { ApplicationConfig, importProvidersFrom, enableProdMode, ErrorHandler, PLATFORM_ID, LOCALE_ID, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig, UrlSerializer, Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withFetch, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { isPlatformBrowser } from '@angular/common';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as Sentry from '@sentry/angular-ivy';

import { ApiInterceptor } from './app/interceptors/api.interceptor';
import { environment } from './environments/environment';

import { Util } from './app/shared/util/util';
import { UserService } from './app/shared/services/user_service';
import { NoteService } from './app/shared/services/note_service';
import { StreamService } from './app/shared/services/stream_service';
import { CacheService } from './app/shared/services/cache_service';
import { ContactService } from './app/shared/services/contact_service';
import { CategoriesService } from './app/shared/services/categories_service';
import { SearchService } from './app/shared/services/search_service';
import { NotificationsService } from './app/shared/services/notifications_service';
import { GeoService } from './app/shared/services/geographic_service';
import { TeamService } from './app/shared/services/team_service';
import { TagService } from './app/shared/services/tag_service';
import { NotificationService } from './app/shared/services/notification_service';
import { MetaService } from './app/shared/services/meta_service';
import { PwaService } from './app/shared/services/pwa_service';
import { A2hsService } from './app/shared/services/a2hs.service';
import { GtmService } from './app/shared/services/gtm_service';
import { AnalyticsService } from './app/shared/services/analytics_service';
import { EnvironmentService } from './app/shared/services/environment_service';

import { StreamIdGuard } from './app/shared/route-guards/stream-id.guard';
import { NoteOwnerGuard } from './app/shared/route-guards/note-owner.guard';
import { GuardQueue } from './app/shared/route-guards/guard-queue.quard';
import { NoteGuideGuard } from './app/shared/route-guards/note-guide.guard';

import { NoteResolve } from './app/shared/resolvers/note-resolve.service';
import { ErrorsHandler } from './app/shared/errors-handler';
import { routes } from './app/app.routes';
import { CustomUrlSerializer } from './app/customUrlSerializer';

import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faCaretDown, faUser, faBell, faTrash, faCaretUp, faInfoCircle, faEnvelope, faBars, faSync, faVolumeUp, faPodcast, faStream,
  faAngleDoubleLeft, faAngleRight, faSyncAlt, faUserPlus, faCircleNotch, faCheckCircle, faExclamationCircle, faExclamationTriangle,
  faTrashAlt, faEllipsisV, faCalendar, faCalendarAlt as fasCalendarAlt, faSpinner, faTimes, faCaretLeft, faCaretRight, faQuestionCircle,
  faTachometerAlt, faUsers, faMoneyCheckDollar, faList, faUserGroup, faComments, faPen, faUserCircle, faSearch, faInfo,
  faMoneyBillAlt, faCheckSquare, faUserCheck, faUserXmark, faLink, faStar, faStarHalfAlt, faBookmark, faThumbsUp, faThumbsDown,
  faDollarSign, faFileUpload, faMapMarker, faCircle, faSquare, faCheck, faSignOutAlt, faSignInAlt, faShare, faChevronLeft,
  faChevronRight, faChevronUp, faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faFacebook, faReddit, faGooglePlus } from '@fortawesome/free-brands-svg-icons';
import {
  faTimesCircle, faCalendarAlt as farCalendarAlt, faBell as farBell, faCheckCircle as farCheckCircle,
  faThumbsUp as farThumbsUp, faThumbsDown as farThumbsDown, faStickyNote as farStickyNote, faIdCard as farIdCard,
  faBookmark as farBookmark, faStar as farStar, faCircle as farCircle
} from '@fortawesome/free-regular-svg-icons';
import { faStickyNote as fasStickyNote } from '@fortawesome/free-solid-svg-icons';
import { LY_THEME, LyCommonModule, LyThemeModule } from '@alyle/ui';
import { MinimaDark, MinimaLight } from '@alyle/ui/themes/minima';

echarts.use([BarChart, GridComponent, CanvasRenderer]);

if (environment.production) {
  enableProdMode();
}

export class SafeFaIconLibrary extends FaIconLibrary {
  override getIconDefinition(prefix: any, name: any): any {
    const icon = super.getIconDefinition(prefix, name);
    if (!icon) {
      console.warn(`FontAwesome: Icon [${prefix}, ${name}] not found in library.`);
      return super.getIconDefinition('fas', 'question-circle');
    }
    return icon;
  }
}

export function sentryErrorHandlerFactory(platformId: string): ErrorHandler {
  const envService = inject(EnvironmentService);

  if (isPlatformBrowser(platformId)) {
    return Sentry.createErrorHandler({
      showDialog: (envService.urlBasePrefix !== 'https://notd.io'),
    });
  } else {
    return new ErrorsHandler(platformId, null as any);
  }
}

export function getSessionStorage() {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    return window.sessionStorage;
  }
  return {
    getItem: () => null,
    setItem: () => { },
    removeItem: () => { },
    clear: () => { },
    length: 0,
    key: () => null
  };
}

export function provideFontAwesomeLibrary() {
  const library = new SafeFaIconLibrary();
  // solid icons
  library.addIcons(
    faCaretDown, faUser, faBell, faTrash, faBars, faInfoCircle, faEnvelope, faCaretUp, faSync, faVolumeUp, faPodcast, faStream,
    faAngleDoubleLeft, faAngleRight, faSyncAlt, faUserPlus, faCircleNotch, faCheckCircle, faExclamationCircle, faExclamationTriangle,
    faTrashAlt, faEllipsisV, faCalendar, fasCalendarAlt, faSpinner, faTimes, faCaretLeft, faCaretRight, faQuestionCircle,
    faTachometerAlt, faUsers, faMoneyCheckDollar, faList, faUserGroup, faComments, faPen, faUserCircle, faSearch, faInfo,
    faMoneyBillAlt, faCheckSquare, faUserCheck, faUserXmark, faLink, faStar, faStarHalfAlt, faBookmark, faThumbsUp, faThumbsDown,
    faDollarSign, faFileUpload, faMapMarker, faCircle, faSquare, faCheck, fasStickyNote, faSignOutAlt, faSignInAlt, faShare,
    faChevronLeft, faChevronRight, faChevronUp, faChevronDown
  );
  // brands
  library.addIcons(faTwitter, faFacebook, faReddit, faGooglePlus);
  // regular
  library.addIcons(
    faTimesCircle, farCalendarAlt, farBell, farCheckCircle, farThumbsUp, farThumbsDown,
    farStickyNote, farIdCard, farBookmark, farStar, farCircle
  );
  return library;
}

export function initializeAppFactory() {
  return () => { };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: ErrorHandler, useFactory: sentryErrorHandlerFactory, deps: [PLATFORM_ID, EnvironmentService] },
    { provide: Sentry.TraceService, deps: [Router] },

    { provide: LOCALE_ID, useValue: 'en' },
    { provide: 'sessionStorage', useFactory: getSessionStorage },
    { provide: APP_INITIALIZER, useFactory: initializeAppFactory, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
    importProvidersFrom(
      FontAwesomeModule,
      NgbModule,
      NgxEchartsModule.forRoot({ echarts }),
      LyThemeModule.setTheme('minima-light'),
      LyCommonModule
    ),
    { provide: LY_THEME, useClass: MinimaLight, multi: true },
    { provide: LY_THEME, useClass: MinimaDark, multi: true },
    {
      provide: FaIconLibrary,
      useFactory: provideFontAwesomeLibrary
    },
    provideServiceWorker('/ngsw-worker.js', { enabled: environment.production }),

    Util, UserService, NoteService, StreamService, CacheService, ContactService, CategoriesService,
    SearchService, NotificationsService, GeoService, TeamService, TagService, NotificationService,
    MetaService, PwaService, A2hsService, GtmService, AnalyticsService, EnvironmentService,
    StreamIdGuard, NoteOwnerGuard, GuardQueue, NoteGuideGuard, NoteResolve
  ]
};
